import { useCallback, useEffect, useState } from 'react'
import { type_GM_STATE } from '../../helpers'
import { emitterProps, globalEmitter } from '../helpers/emitter'
import { closeModal, openModal } from '../helpers/useModal'
import { PlusOutlineIcon } from '../icons/PlusOutlineIcon'
import { OmneoList, OmneoListItemMeta, type_WISHLIST_DASHBOARD_STATE, typeProduct, typeVariant } from '../types'
import { wishlistProviderService } from '../helpers/provider.service'
import { Modal } from './Modal'
import { getWishlistListCoverImage } from '../helpers/utils'
import { LoaderSpin } from './LoaderSpin'
import { WishlistListRow } from './WishlistListRow'
import classNames from 'classnames'

declare var GM_STATE: type_GM_STATE
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE

type props = {
  product: typeProduct | null | undefined
  productBiline: string
  variantSelectedLocal: any
  variantSelectedId: number
  templateSuffix: string | null
  propertiesLocal?: any
  productPersonalisedPrice?: string
}

export function WishlistSlideout({
  product,
  productBiline,
  variantSelectedLocal,
  variantSelectedId,
  templateSuffix,
  propertiesLocal,
  productPersonalisedPrice = '0'
}: props) {
  const [variantSelected, setVariantSelected] = useState<typeVariant | null>(null)

  useEffect(() => {
    if (!product) {
      setVariantSelected(null)
      return
    }

    const variant = product.variants.find((variant: any) => variant.id.toString() === variantSelectedId.toString()) || variantSelectedLocal
    if (variant) {
      setVariantSelected(variant)
    }
  }, [product, variantSelectedId, variantSelectedLocal])

  if (!product) return null

  return (
    <Modal
      id="wishlist-slideout"
      className="modal-wishlist"
      content={
        <WishlistListPickerContent
          product={product}
          productBiline={productBiline}
          variantSelected={variantSelected}
          templateSuffix={templateSuffix}
          propertiesLocalInfo={propertiesLocal}
          productPersonalisedPrice={productPersonalisedPrice}
          modalId="wishlist-slideout"
        />
      }
      type="slideout"
      renderOnOpen
    />
  )
}

export function WishlistListPickerContent({
  product,
  productBiline,
  variantSelected,
  templateSuffix,
  propertiesLocalInfo,
  productPersonalisedPrice,
  modalId = 'wishlist-slideout',
  showHeading = true
}: {
  product: typeProduct
  productBiline: string
  variantSelected: typeVariant | null
  templateSuffix: string | null
  propertiesLocalInfo: any
  productPersonalisedPrice: string
  modalId?: string
  showHeading?: boolean
}) {
  const [lists, setLists] = useState<OmneoList[]>([])
  const [changeList, setChangeList] = useState(true)
  const [propertiesLocal, setPropertiesLocal] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const handleAddToWishlist = (event: Event) => {
      const customEvent = event as CustomEvent<{ addQueue: any }>
      setPropertiesLocal(customEvent.detail.addQueue)
    }

    document.addEventListener('product:addToWishlist', handleAddToWishlist)

    return () => {
      document.removeEventListener('product:addToWishlist', handleAddToWishlist)
    }
  }, [])

  useEffect(() => {
    if (propertiesLocalInfo) {
      setPropertiesLocal(propertiesLocalInfo)
    }
  }, [propertiesLocalInfo])

  const getMinimumProps = useCallback((): OmneoListItemMeta | null => {
    if (!variantSelected) {
      console.error('Item must have a selected variant to be added to a list', variantSelected)
      return null
    }
    const minimumProps: OmneoListItemMeta = {
      handle: product.handle,
      image: {
        src: getImage() || '',
        alt: product.title
      },
      productBiline,
      productId: product.id,
      variantId: variantSelected.id,
      variantPrice: variantSelected.price,
      variantSku: variantSelected.sku,
      variantOptions: [variantSelected.option1, variantSelected.option2, variantSelected.option3],
      variantAvailable: variantSelected.available,
      templateSuffix,
      properties: propertiesLocal,
      productPersonalisedPrice
    }
    return minimumProps
  }, [product, productBiline, variantSelected, templateSuffix, propertiesLocal, productPersonalisedPrice])

  const fetchLists = async () => {
    setIsLoading(true)
    const lists = await wishlistProviderService.getLists()
    setLists(lists)
    setIsLoading(false)
  }

  const getImage = () => {
    if (propertiesLocal.some((property: any) => property.hasOwnProperty('pair'))) {
      if (propertiesLocal.some((property: any) => property.pair === 'true')) {
        return product.featured_image
      } else {
        return variantSelected?.featured_image?.src
      }
    } else {
      return variantSelected?.featured_image?.src || product.images[0]
    }
  }

  const addItem = async (list: OmneoList) => {
    if (!list || !product || !variantSelected) {
      globalEmitter.emit('triggerNotification', { message: 'Item could not be added to wishlist', type: 'error' })
      return
    }
    const minimumProps = getMinimumProps()
    if (!minimumProps) {
      globalEmitter.emit('triggerNotification', { message: 'Item could not be added to wishlist', type: 'error' })
      return
    }

    const wishlistItem = wishlistProviderService.buildListItem(minimumProps)
    const result = await wishlistProviderService.addItemToList(list, wishlistItem)
    if (!result) {
      globalEmitter.emit('triggerNotification', { message: 'Item could not be added to wishlist', type: 'error' })
      return
    }

    globalEmitter.emit('triggerNotification', { message: 'Item successfully added to wishlist', type: 'success' })
    closeModal(modalId)
    globalEmitter.emit(emitterProps.wishlist.add)
    setChangeList(!changeList)
  }

  const openCreateList = async () => {
    const minimumProps = getMinimumProps()
    const modalContext = minimumProps
      ? { item: wishlistProviderService.buildListItem(minimumProps), parentModalId: modalId } //
      : null

    openModal('create-list-slideout', modalContext)
  }

  useEffect(() => {
    const handleListChange = () => {
      setChangeList((currentValue) => !currentValue)
    }

    const handleCreateNewList = () => {
      handleListChange()
    }

    const handleWishlistRemove = () => {
      handleListChange()
    }

    document.addEventListener('createNewList', handleCreateNewList)
    document.addEventListener(emitterProps.wishlist.remove, handleWishlistRemove)

    return () => {
      document.removeEventListener('createNewList', handleCreateNewList)
      document.removeEventListener(emitterProps.wishlist.remove, handleWishlistRemove)
    }
  }, [])

  useEffect(() => {
    if (wishlistProviderService.isReady()) {
      fetchLists().then()
    }
  }, [changeList])

  return (
    <div className="">
      {showHeading && <h3 className="h6 mb-2 text-[14px] uppercase text-ink lg:mb-5 lg:text-[16px]">{wishlistDashboardState.chooseWishlist}</h3>}
      <LoaderSpin show={isLoading && lists.length === 0} />
      {lists.length > 0 &&
        lists.map((list, index) => {
          const image = getWishlistListCoverImage(list)

          // indicates the src does not have a protocol
          if (image.src.slice(0, 5) !== 'https' && image.src.slice(0, 2) === '//') {
            image.src = `https:${image.src}`
          }

          // attempt to resize image
          sizeImageCorrectly: try {
            const imageUrl = new URL(image.src)
            const params = imageUrl.searchParams
            if (params.get('width')) break sizeImageCorrectly

            // approx double size required; better than a 4k image
            params.set('width', '160')
            image.src = imageUrl.toString()
          } catch (err) {
            console.warn('Attempted to add width param to this src', { src: image.src, err })
          }

          return (
            <div className={classNames("w-full py-[15px] border-0 border-solid border-secondary md:py-5", index !== 0 && "border-t")} key={list.id}>
              <WishlistListRow
                list={list}
                image={image}
                onClick={() => addItem(list)}
                className="flex items-center w-full cursor-pointer"
                imageWrapperClassName="relative aspect-[84/102] w-[84px] bg-grey-900"
                contentClassName="flex-1 ml-6"
                chevronClassName="w-3 h-3 text-ink"
                trailingIcon="plus"
              />
            </div>
          )
        })}
      <div className="w-full py-[15px] border-0 border-y border-solid border-secondary md:py-5">
        <div className="flex items-center w-full cursor-pointer" onClick={openCreateList}>
          <div className="flex-1">
            <h4 className="m-0 text-[14px] lg:text-[16px] text-ink font-light font-sans uppercase">{wishlistDashboardState.createNewList}</h4>
          </div>
          <PlusOutlineIcon className="w-3 h-3 text-ink" />
        </div>
      </div>
    </div>
  )
}
